import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useApi from '../api';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { DirectoryUser, Job, OrgUser, apiErrorMessage } from '../services/servicesShared';
import { StewardsSelect } from '../services/StewardsSelect';
import { DirectorySearch } from './DirectorySearch';

/** PUT /jobs/{id}/owner — the accountable customer user (must be a member of the job's workspace). */
export const ChangeOwnerDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    job: Job;
    onChanged: (job: Job) => void;
}> = ({ open, onClose, job, onChanged }) => {
    const { put } = useApi();
    const [user, setUser] = useState<DirectoryUser | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) setUser(null);
    }, [open]);

    const save = async () => {
        if (!user) return;
        setSaving(true);
        try {
            const response = await put(`/jobs/${job.id}/owner`, { customer_user_id: user.id });
            onChanged(response.data);
            toast.success('Customer contact changed');
            onClose();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Could not change the customer contact'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Change the customer contact</DialogTitle>
                    <DialogDescription>
                        The owner is accountable for the job and the identity it acts as.
                        {job.scope === 'tenant' ? ' They must be an active member of this job’s workspace.' : ''}
                    </DialogDescription>
                </DialogHeader>
                <DirectorySearch selected={user} onSelect={setUser} autoFocus />
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button onClick={save} disabled={!user || saving}>
                        {saving ? 'Saving…' : 'Make them the contact'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

/** PUT /jobs/{id}/stewards — override the service's stewards for this job, or inherit. */
export const StewardsDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    job: Job;
    serviceStewardIds: string[];
    users: OrgUser[];
    usersLoading: boolean;
    onChanged: (job: Job) => void;
}> = ({ open, onClose, job, serviceStewardIds, users, usersLoading, onChanged }) => {
    const { put } = useApi();
    const [inherit, setInherit] = useState(job.steward_user_ids == null);
    const [ids, setIds] = useState<string[]>(job.steward_user_ids || serviceStewardIds);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setInherit(job.steward_user_ids == null);
            setIds(job.steward_user_ids || serviceStewardIds);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const save = async () => {
        setSaving(true);
        try {
            const response = await put(`/jobs/${job.id}/stewards`, { steward_user_ids: inherit ? null : ids });
            onChanged(response.data);
            toast.success('Team updated');
            onClose();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Could not update the team'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Your team on this job</DialogTitle>
                    <DialogDescription>
                        The people on your team who answer this job's questions and approvals. By default, the service's team.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <label className="flex items-center gap-2 text-sm">
                        <Switch checked={inherit} onCheckedChange={setInherit} />
                        <span>Use the service's team</span>
                    </label>
                    {!inherit && <StewardsSelect value={ids} onChange={setIds} users={users} loading={usersLoading} />}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button onClick={save} disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
